<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useMagicKeys } from '@vueuse/core';
import { useUser } from '@/login/composables/useUser';
import type { InstancesEntityData } from '@/login/types/AccessTokenData';
import TemplateLoginCard from '@/views/newlogin/templates/TemplateLoginCard.vue';
import { useLoginManager } from '@/login/composables/useLoginManager';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { useQueryClient } from '@tanstack/vue-query';
import { useFuzzySearch } from '@/composables/useFuzzySearch';
import { useRouterHelper } from '@/composables/useRouterHelper';
import { useRouter } from 'vue-router';

const manageInstances = ref(false);
const { alt } = useMagicKeys();
const queryClient = useQueryClient();
const loginStore = useLoginStore();
const { instances, isInstanceActive } = useUser();
const { logout } = useLoginManager();
const { userTypeStartPageRoute } = useRouterHelper();
const router = useRouter();

/**
 * handle on instance click.
 * @param instance clicked instance.
 */
function selectInstance(instance: InstancesEntityData): void {
    loginStore.setInstance(instance.SubDomain);
    void router.push(userTypeStartPageRoute());
    void queryClient.cancelQueries({ fetchStatus: 'fetching' });
    queryClient.clear();
}

/**
 * Check if the instance is a localhost or ??? instance.
 * @param instance instance to check
 */
function checkInstanceName(instance?: string | null): boolean {
    if (instance === 'localhost' || instance === '???') {
        return false;
    }
    return true;
}

/**
 * filter instances
 */

const sortedInstances = computed(() => instances.value
    .toSorted((a, b) => (a.Name ?? '').trim().localeCompare((b.Name ?? '').trim(), undefined, { numeric: true, sensitivity: 'base' })));

const { searchString, filteredItems } = useFuzzySearch(sortedInstances.value, { keys: ['Name', 'SubDomain'] });

function onKeyEnter(): void {
    if (filteredItems.value.length > 0) {
        selectInstance(filteredItems.value[0]);
    }
}

const cardSize = computed(() => (manageInstances.value ? 'lg' : 'md'));

</script>

<template>
    <!-- Choose instance -->
    <TemplateLoginCard :size="cardSize">
        <SText
            is="p"
            size="4"
            class="mb-8"
        >
            {{ $t('components.login.selectInstanceAccountDescription') }}
        </SText>
        <SSearchBar
            v-model="searchString"
            :autofocus="true"
            class="mb-5"
            @keydown.enter="onKeyEnter"
        />
        <div style="height: 45vh" class="overflow-auto mb-4">
            <template
                v-for="instance in filteredItems"
                :key="`instance-${instance.SubDomain}-${instance.Name}`"
            >
                <SButtonGroup
                    v-if="alt || !instance.HideFromInstanceList"
                    v-tooltip="!isInstanceActive(instance.SubDomain) ? $t('components.login.instanceInactive') : ''"
                    class="w-100 mb-3"
                    aria-label="Instance list"
                    role="group"
                >
                    <SButton
                        variant-type="outline"
                        variant="secondary-light"
                        justify-content="start"
                        class="text-start w-75"
                        :disabled="isInstanceActive(instance.SubDomain) === false"
                        @click="selectInstance(instance)"
                    >
                        <SStack v-if="checkInstanceName(instance.Name)" vertical gap="2">
                            <SText weight="medium" size="4" class="text-wrap">{{ instance.Name }}</SText>
                            <SText variant="secondary" size="4" class="text-wrap">{{ instance.SubDomain }}</SText>
                        </SStack>
                        <SText v-else>{{ instance.SubDomain.replace('.sysarb.app', '') }}</SText>
                    </SButton>
                </SButtonGroup>
            </template>
        </div>
        <SText is="p" v-if="instances.length === 0" class="text-center">{{ $t('system.accessToNoServerInstances') }}</SText>
        <SStack gap="5" class="mt-6" justify-content="center">
            <SButton
                variant="secondary-light"
                @click="logout"
            >
                {{ $t('system.logout') }}
            </SButton>
        </SStack>
    </TemplateLoginCard>
</template>
